import { ITEM_CALCULATED_FIELDS } from "appConfig";

import { Decimal } from "classes/DecimalClasses";

import {
  get,
  getRecord,
  getRecordWithParams,
  getItemBom,
  getByIds,
  calculateBomCostBasis,
} from "services/sosInventoryService/sosApi";
import { filterAndFormatBinOptions } from "services/utility/bins";
import {
  dateToSosISODateTime,
  removeDateSeconds,
} from "services/utility/dates";
import { setPageDirty } from "services/utility/edit";
import { handleProgramError } from "services/utility/errors";
import { formatContact } from "services/utility/formatting";
import { normalizeSerials } from "services/utility/serials";
import {
  getBaseUom,
  getUomConversionFromUomReference,
} from "services/utility/uoms";

import globalState from "globalState/globalState";
import {
  editModalLoadingIndicatorOn,
  editModalLoadingIndicatorOff,
} from "globalState/loadingSlice";

import { ITEM_TYPES } from "appConstants";
import { EMPTY_LINE_ITEM } from "editConfig";

export function isInventoryItemAssemblyOrCategory(type) {
  return (
    type === ITEM_TYPES.INVENTORY_ITEM ||
    type === ITEM_TYPES.ASSEMBLY ||
    type === ITEM_TYPES.CATEGORY
  );
}

export function isInventoryItemOrAssembly(type) {
  return type === ITEM_TYPES.INVENTORY_ITEM || type === ITEM_TYPES.ASSEMBLY;
}

// determine the best units of measure to use; of the units passed in,
// favor in this order: kg, lb, oz, g
export function determineBestUnitToDisplay(units) {
  if (units.includes("kg")) {
    return "kg";
  }
  if (units.includes("lb")) {
    return "lb";
  }
  if (units.includes("oz")) {
    return "oz";
  }
  return "g";
}

/** @name    getItemRecord
 *
 * @summary retrieves an item record, using one of two sosApi functions,
 * depending on whether we can specify a location (and thus get the
 * locationBins property) or not
 *
 * @param id (number) - item id
 *
 * @param location (location reference object or null)
 *
 * @param transactionDateTime (Date) - the date/time of the relevant
 * transaction; will be used to populate the locationBins property with
 * the correct inventory quantity at that date/time
 *
 * @return item record (with locationBins property, if location  was
 * specified)
 */
export async function getItemRecord(
  itemId,
  locationId,
  transactionDateTime,
  columns
) {
  try {
    return await getRecordWithParams("item", itemId, {
      locationId,
      dt: transactionDateTime
        ? dateToSosISODateTime(removeDateSeconds(transactionDateTime))
        : "",
      inTransaction: true,
      columns: columns ? columns.join(",") : "",
    });
  } catch (e) {
    handleProgramError(e);
  }
}

export async function getMultipleItemRecords(
  itemIdArray,
  locationId = "",
  transactionDateTime,
  columns
) {
  try {
    return await getByIds("item", itemIdArray, columns, {
      locationId,
      dt: transactionDateTime
        ? dateToSosISODateTime(transactionDateTime || new Date())
        : "",
      inTransaction: true,
    });
  } catch (e) {
    handleProgramError(e);
  }
}

/**
 * @name    extractBillingAndShippingFromCustomer
 *
 * @summary combines a number of customer fields into *billing* and
 *          *shipping* fields suitable for hydration of the respective
 *          fields in a transaction record
 *
 * @param   customer (Object) - a customer object as it comes back from
 *          the API
 *
 * @returns { billing, shipping } (Object) - in format used by transactions
 */
export function extractBillingAndShippingFromCustomer(customer) {
  const { billing, shipping, contact, companyName, phone, email } =
    customer || {};

  const contactDetails = {
    company: companyName,
    contact: formatContact(contact),
    phone,
    email,
  };

  const billingAddress = {
    address: billing,
    ...contactDetails,
  };

  const shippingAddress = {
    address: shipping,
    ...contactDetails,
  };

  return { billing: billingAddress, shipping: shippingAddress };
}

// updates line.onhand, assuming that line.relatedRecords.item is current
export function updateLineItemOnhand(lines) {
  return lines.map((line) => {
    const item = line.relatedRecords.item;
    if (item) {
      const conversion = getUomConversionFromUomReference(line.uom, item.uoms);
      const onhand = item.onhand.div(conversion).round(2, Decimal.roundDown);
      return { ...line, onhand };
    } else {
      return { ...line, onhand: null };
    }
  });
}

// updates line.relatedRecords.item for all lines, based on item, location,
// and date
export async function updateLineRelatedRecordsItem(
  location,
  date,
  lines,
  itemFieldsNeeded
) {
  return await Promise.all(
    lines.map(async (line) => {
      if (line.item?.id) {
        const item = await getItemRecord(
          line.item.id,
          location?.id,
          date,
          itemFieldsNeeded
        );
        return { ...line, relatedRecords: { ...line.relatedRecords, item } };
      }
      return line;
    })
  );
}

export function removeLotAndSerialValues(lineItems) {
  return lineItems.map((lineItem) => ({
    ...lineItem,
    lot: null,
    serials: null,
  }));
}

// get all bins for a location
export async function getAllBinsForLocation(locationId) {
  const location = await getRecord("location", locationId);
  return location.bins;
}

export function createLineFromItem(item, emptyLineData, lineNumber) {
  return {
    ...emptyLineData,
    lineNumber,
    item: { id: item.id, name: item.name },
    class: item.class,
    onhand: item.onhand,
    description: item.description,
    unitprice: item.baseSalesPrice,
    relatedRecords: { item },
    itemDetails: {
      serialTracking: item.serialTracking,
      lotTracking: item.lotTracking,
      itemWeight: item.weight,
      itemVolume: item.volume,
      itemUoms: item.uoms,
      type: item.type,
      useMarkup: item.useMarkup,
      markupPercent: item.markupPercent,
      baseSalesPrice: item.baseSalesPrice,
    },
  };
}

export async function getAndSortWorkCenters() {
  const response = await get("workcenter", { inTransaction: true });
  if (response.success) {
    return response.data.records.sort((a, b) => {
      if (a.id === 1) {
        return 1;
      }
      return a.sortOrder < b.sortOrder ? -1 : 1;
    });
  } else {
    handleProgramError(
      new Error(
        `getAndSortWorkCenters | unsuccessful call to getAll, message: ${response.message}`
      )
    );
  }
}

export function clearItemDataFromLine(line) {
  const item = { id: null, name: null };
  const relatedRecords = { ...line.relatedRecords, item: null };
  return { ...line, item, relatedRecords, uom: null, itemDetails: {} };
}

export async function expandItemGroup(
  lineToExpand,
  lineHandler,
  objectType,
  record
) {
  globalState.dispatch(editModalLoadingIndicatorOn());
  const bom = await getItemBom(lineToExpand.item.id);
  const bomIds = bom.map((bomItem) => bomItem.componentItem.id);
  const params = {
    locationId: record.location?.id,
    dt: dateToSosISODateTime(record.date),
  };

  const data = await getByIds(
    "item",
    bomIds,
    ITEM_CALCULATED_FIELDS[objectType],
    params
  );
  lineHandler({ type: "delete", deleteAt: lineToExpand.lineNumber });

  const lines = bom
    .map((bomItem, i) => {
      const item = data.find(({ id }) => id === bomItem.componentItem.id);
      if (!item) {
        return null;
      }

      const availableBins = item.locationBins
        ? filterAndFormatBinOptions(item.locationBins)
        : null;

      return {
        ...lineToExpand,
        lineNumber: lineToExpand.lineNumber + i,
        item: bomItem.componentItem,
        description: item.description,
        weightunit: item.weightUnit,
        volumeunit: item.volumeUnit,
        listPrice: item.salesPrice,
        uom: getBaseUom(item.uoms),
        vendorPartNumber: item.vendorPartNumber,
        basePurchaseCost: item.basePurchaseCost,
        quantity: bomItem.quantity.times(lineToExpand.quantity),
        unitprice: item.baseSalesPrice,
        amount: EMPTY_LINE_ITEM[objectType].amount,
        relatedRecords: { item },
        onhand: item.onhand,
        available: item.available,
        availableBins,
        bin: null,
        itemDetails: {
          itemWeight: item.weight,
          itemVolume: item.volume,
          itemUoms: item.uoms,
          type: item.type,
          useMarkup: item.useMarkup,
          markupPercent: item.markupPercent,
          baseSalesPrice: item.baseSalesPrice,
          serialTracking: item.serialTracking,
          lotTracking: item.lotTracking,
        },
      };
    })
    .filter((line) => line);

  setPageDirty();
  lineHandler({
    type: "insertMany",
    insertAt: lineToExpand.lineNumber,
    lines,
  });
  globalState.dispatch(editModalLoadingIndicatorOff());
}

export async function expandBomItemGroup(itemToExpand) {
  const bom = await getItemBom(itemToExpand.componentItem.id);
  const bomIds = bom.map((bomItem) => bomItem.componentItem.id);
  const data = await getByIds("item", bomIds);

  return await Promise.all(
    bom
      .map(async (bomItem) => {
        const item = data.find(({ id }) => id === bomItem.componentItem.id);
        const { costBasis } = await calculateBomCostBasis(item.id);
        if (item) {
          const newLine = {
            componentItem: bomItem.componentItem,
            description: item.description,
            quantity: bomItem.quantity.times(itemToExpand.quantity),
            cost: costBasis,
            total: costBasis.times(bomItem.quantity),
            typeOfItem: item.type,
          };
          return newLine;
        }
        return null;
      })
      .filter((bomItem) => bomItem)
  );
}

export function updateLineItemSerials(autoSerialLotNumbering, lines) {
  return lines.map((line) => {
    const { serials, quantity, itemDetails } = line;
    return {
      ...line,
      serials: normalizeSerials(
        serials,
        quantity,
        itemDetails.serialTracking,
        autoSerialLotNumbering
      ),
    };
  });
}
